import { useTranslation } from 'react-i18next';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import CollapsiblePanel from './CollapsiblePanel.js';
import { formatCurrency } from '../../../utils/formatCurrency.js';
import {
  HubCategoryTick,
  hubAxisMaxChars,
  hubAxisWidth,
  hubChartHeight,
  useChartBreakpoint,
} from './chartAxis.js';
import {
  CHART_TOOLTIP_WRAPPER_PROPS,
  DashboardAmountTooltip,
} from './chartTheme.js';

const METRICS = [
  { id: 'net', labelKey: 'dashboard.netAmount' },
  { id: 'freight', labelKey: 'dashboard.freightAmount' },
  { id: 'gc_count', labelKey: 'dashboard.gcCount' },
];

/** Soft DigiQin fills: a bit light, not washed out. */
export const BOOKING_PAYMENT_COLORS = {
  on_account: '#86B4DB',
  paid: '#7EC8A8',
  to_pay: '#E2B86A',
};

const ChartSpinner = () => (
  <div className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center rounded-lg">
    <div className="w-8 h-8 border-[3px] border-gray-200 border-t-gray-600 rounded-full animate-spin" />
  </div>
);

const BookingSummaryChart = ({
  data,
  metric = 'net',
  onMetricChange,
  onDateChange,
  loading = false,
  hubFilter = null,
  className = '',
  embedded = false,
}) => {
  const { t } = useTranslation();
  const breakpoint = useChartBreakpoint();
  const hubs = data?.hubs || [];
  const chips = data?.day_chips || [];
  const selectedDate = data?.summary_date;
  const isCount = metric === 'gc_count';
  const labelWidth = hubAxisWidth(breakpoint);
  const chartHeight = hubChartHeight(hubs.length, breakpoint);

  const heading = `${t('dashboard.bookingSummaryByHub')}${data?.summary_date ? ` (${data.summary_date})` : ''}`;
  const body = (
    <>
      {!embedded ? null : <h3 className="text-sm font-semibold text-gray-800 mb-3">{heading}</h3>}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:thin]">
        {chips.map((chip) => (
          <button
            key={chip.date}
            type="button"
            onClick={() => onDateChange?.(chip.date)}
            className={`px-2.5 py-1.5 sm:py-1 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 ${
              chip.date === selectedDate
                ? 'bg-emerald-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {METRICS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onMetricChange?.(item.id)}
            className={`px-3 py-1 rounded-lg text-xs font-medium ${
              metric === item.id
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>
      {hubs.length === 0 ? (
        <div className="relative">
          {loading && <ChartSpinner />}
          <p className="text-sm text-gray-400 text-center py-10">{t('common.noData')}</p>
        </div>
      ) : (
        <div className="relative w-full overflow-x-auto" style={{ height: chartHeight }}>
          {loading && <ChartSpinner />}
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={hubs}
              layout="vertical"
              margin={{ top: 8, right: 12, left: 4, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                type="number"
                tick={{ fontSize: breakpoint === 'sm' ? 10 : 12 }}
                tickFormatter={(v) => (isCount ? v : formatCurrency(v))}
              />
              <YAxis
                type="category"
                dataKey="hub_name"
                width={labelWidth}
                interval={0}
                tick={<HubCategoryTick maxChars={hubAxisMaxChars(breakpoint)} />}
              />
              <Tooltip
                {...CHART_TOOLTIP_WRAPPER_PROPS}
                content={<DashboardAmountTooltip format={isCount ? 'count' : 'currency'} />}
              />
              <Legend />
              <Bar dataKey="on_account" stackId="a" fill={BOOKING_PAYMENT_COLORS.on_account} name={t('dashboard.onAccount')} />
              <Bar dataKey="paid" stackId="a" fill={BOOKING_PAYMENT_COLORS.paid} name={t('tables.paid')} />
              <Bar dataKey="to_pay" stackId="a" fill={BOOKING_PAYMENT_COLORS.to_pay} name={t('dashboard.toPay')} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div className={className}>{body}</div>;
  }

  return (
    <CollapsiblePanel title={heading} defaultOpen trailing={hubFilter} className={className}>
      {body}
    </CollapsiblePanel>
  );
};

export default BookingSummaryChart;
